/**
 * Thin fetch wrapper for the Laravel backend.
 *
 * The site is built so that it still renders if the API is unreachable: every
 * helper resolves to `null` on failure and the components fall back to the
 * content bundled with the build. That keeps the public site up even during a
 * backend deployment.
 */

const RAW_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
export const API_BASE = RAW_BASE;
export const API_URL = RAW_BASE ? `${RAW_BASE}/api/v1` : '';
export const API_ENABLED = RAW_BASE !== '' && process.env.REACT_APP_USE_API !== 'false';

/** Turns a stored path into a usable URL, whatever form the backend returned. */
export function mediaUrl(path) {
  if (!path) return null;
  if (/^(https?:)?\/\//.test(path)) return path;
  if (path.startsWith('/')) return path;              // already site-relative
  if (path.startsWith('assets/')) return `/${path}`;  // bundled with this build
  return API_BASE ? `${API_BASE}/storage/${path}` : `/${path}`;
}

const DEFAULT_TIMEOUT = 12000;

async function request(path, { params, timeout = DEFAULT_TIMEOUT, ...options } = {}) {
  if (!API_ENABLED) return null;

  const url = new URL(`${API_URL}${path}`);

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json', ...(options.headers || {}) },
      signal: controller.signal,
      ...options,
    });

    if (!response.ok) {
      const error = new Error(`Request failed with status ${response.status}`);
      error.status = response.status;

      try {
        error.payload = await response.json();
      } catch {
        error.payload = null;
      }

      throw error;
    }

    return await response.json();
  } finally {
    clearTimeout(timer);
  }
}

/** GET that never throws — returns `fallback` when the API is down. */
export async function getData(path, { params, fallback = null } = {}) {
  try {
    const json = await request(path, { params });
    if (!json) return fallback;
    return json.data !== undefined ? json.data : json;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(`[api] ${path} unavailable — using bundled content.`, error.message);
    }
    return fallback;
  }
}

/** POST that reports failures, for forms where the visitor needs to know. */
export async function postData(path, body, { isForm = false } = {}) {
  const json = await request(path, {
    method: 'POST',
    headers: isForm ? {} : { 'Content-Type': 'application/json' },
    body: isForm ? body : JSON.stringify(body),
  });

  if (!json) {
    throw new Error('The backend is not configured for this site.');
  }

  return json;
}

export function currentLocale() {
  try {
    const match = document.cookie.match(/(?:^|;\s*)i18next=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : 'en';
  } catch {
    return 'en';
  }
}

export default { getData, postData, mediaUrl, API_ENABLED, API_URL, API_BASE };
